import * as yup from 'yup';
import mapValues from 'lodash/mapValues';
import { validationMode, validationMessages } from 'helpers/form';
import { CalculationFrequency } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';

export const absoluteReturnTimeWindowShape = yup.object().shape({
  fromDate: yup
    .date()
    .typeError(validationMessages.required)
    .required(validationMessages.required),
  returnComponent: yup.string().when('noReturnComponent', {
    is: (noReturnComponent: boolean) => !noReturnComponent,
    then: yup.string().required(validationMessages.required),
    otherwise: yup.string(),
  }),
  basisPointAdjustment: yup
    .number()
    .integer(validationMessages.integer)
    .typeError(validationMessages.integer)
    .nullable(true)
    .required(validationMessages.required),
  noReturnComponent: yup.bool(),
});

/**
 * Validation Scheme used for Absolute Return Benchmarks
 * @param mode Create | Edit - change the shape of the schema object based on Creating/Editing a benchmark.
 * @returns return yup schema object
 */
export const absoluteReturnValidationSchema = (
  mode: validationMode = validationMode.Create
) => {
  return yup.lazy((obj) =>
    yup.object(
      mapValues(obj, (value, key) => {
        switch (key) {
          case 'timeWindows':
            return mode === validationMode.Create
              ? yup.array().of(absoluteReturnTimeWindowShape)
              : yup.mixed().notRequired();
          case 'name':
            return yup
              .string()
              .trim()
              .required(validationMessages.benchmarkNameNotEmpty);
          case 'calculationFrequencyId':
            return yup
              .number()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'baseCurrencyCode':
            return yup
              .string()
              .typeError(validationMessages.required)
              .required(validationMessages.required);
          case 'numberOfDaysInYearId':
            return yup
              .number()
              .transform((value) => (isNaN(value) ? undefined : value))
              .nullable(true)
              .when('calculationFrequencyId', {
                is: CalculationFrequency.day,
                then: (schema) => schema.required(),
                otherwise: (schema) => schema.notRequired(),
              });
          case 'customLagDate':
            return yup.string().when(['isLagged', 'setCustomLagDate'], {
              is: (isLagged: string, setCustomLagDate: string) =>
                isLagged === 'true' && setCustomLagDate === 'false',
              then: (schema) => schema.required('Please enter a valid date'),
              otherwise: (schema) => schema.notRequired(),
            });
          case 'description':
            return yup
              .string()
              .notRequired()
              .max(350, validationMessages.notExceed350);
          default:
            return yup.mixed().notRequired();
        }
      })
    )
  );
};
